/**
 * Delivery Settings Component
 * ============================
 * 
 * Allows tenant to configure tip deduction percentage
 */

import React, { useState, useEffect } from 'react';
import { Truck, Euro, Info, Save } from 'lucide-react';
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import api from '../utils/api';

const DeliverySettings = () => {
  const [settings, setSettings] = useState({
    tip_deduction_percent: 0,
    tip_deduction_reason: 'VAT'
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    loadSettings();
  }, []);

  const loadSettings = async () => {
    try {
      setLoading(true);
      const response = await api.get('/api/delivery/tenant/settings/delivery');
      
      if (response.data.success) {
        setSettings(response.data.settings);
      }
    } catch (error) {
      console.error('Error loading delivery settings:', error);
      // Settings might not exist yet - use defaults
      setSettings({
        tip_deduction_percent: 0,
        tip_deduction_reason: 'None'
      });
    } finally {
      setLoading(false);
    }
  };

  const saveSettings = async () => {
    try {
      setSaving(true);
      
      const response = await api.patch(
        '/api/delivery/tenant/settings/delivery',
        settings
      );

      if (response.data.success) {
        toast.success('Delivery Einstellungen gespeichert');
      }
    } catch (error) {
      console.error('Error saving delivery settings:', error);
      toast.error('Fehler beim Speichern');
    } finally {
      setSaving(false);
    }
  };

  const presets = [
    { value: 0, label: 'Keine Abzüge (0%)' },
    { value: 7, label: 'Reduziert (7%)' },
    { value: 19, label: 'Regulär (19%)' },
    { value: -1, label: 'Benutzerdefiniert' }
  ];

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <Card className="bg-slate-800/50 border-slate-700">
        <CardHeader>
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-blue-500/20 flex items-center justify-center">
              <Truck className="w-5 h-5 text-blue-400" />
            </div>
            <div>
              <CardTitle className="text-slate-100">Delivery & Trinkgeld</CardTitle>
              <CardDescription className="text-slate-400">
                Konfiguriere Trinkgeld-Abzüge für Fahrer-Abrechnungen
              </CardDescription>
            </div>
          </div>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* Info Box */}
          <div className="bg-blue-500/10 border border-blue-500/30 rounded-lg p-4">
            <div className="flex items-start gap-3">
              <Info className="w-5 h-5 text-blue-400 flex-shrink-0 mt-0.5" />
              <div className="text-sm text-slate-300">
                <p className="font-semibold mb-1">Wie funktioniert der Abzug?</p>
                <p className="text-slate-400">
                  Dieser Prozentsatz wird vom <strong>Online-Trinkgeld</strong> abgezogen, 
                  um steuerliche Rückstellungen oder Provider-Gebühren zu berücksichtigen.
                  Der Fahrer erhält das <strong>Netto-Trinkgeld</strong> als Guthaben bei der Abrechnung.
                </p>
                <p className="text-slate-400 mt-2">
                  <strong>Beispiel:</strong> Trinkgeld €10 mit 19% Abzug → Fahrer erhält €8,10
                </p>
              </div>
            </div>
          </div>

          {/* Preset Selection */}
          <div className="space-y-3">
            <Label className="text-slate-300">Abzug-Voreinstellung</Label>
            <Select
              value={
                presets.find(p => p.value === settings.tip_deduction_percent)
                  ? String(settings.tip_deduction_percent)
                  : '-1'
              }
              onValueChange={(value) => {
                const numValue = parseFloat(value);
                if (numValue >= 0) {
                  setSettings({ ...settings, tip_deduction_percent: numValue });
                }
              }}
            >
              <SelectTrigger className="bg-slate-900 border-slate-700 text-slate-200">
                <SelectValue placeholder="Wähle Voreinstellung" />
              </SelectTrigger>
              <SelectContent className="bg-slate-800 border-slate-700">
                {presets.map(preset => (
                  <SelectItem 
                    key={preset.value} 
                    value={String(preset.value)}
                    className="text-slate-200 focus:bg-slate-700"
                  >
                    {preset.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Custom Percentage Input */}
          <div className="space-y-3">
            <Label className="text-slate-300">Abzug-Prozentsatz (%)</Label>
            <div className="flex items-center gap-3">
              <Input
                type="number"
                min="0"
                max="100"
                step="0.1"
                value={settings.tip_deduction_percent}
                onChange={(e) => setSettings({
                  ...settings,
                  tip_deduction_percent: parseFloat(e.target.value) || 0
                })}
                className="bg-slate-900 border-slate-700 text-slate-200"
                placeholder="0"
              />
              <span className="text-slate-400 text-sm">%</span>
            </div>
            <p className="text-slate-500 text-xs">
              Gültige Werte: 0% - 100% (z.B. 19 für Umsatzsteuer)
            </p>
          </div>

          {/* Reason Input */}
          <div className="space-y-3">
            <Label className="text-slate-300">Abzug-Grund (optional)</Label>
            <Select
              value={settings.tip_deduction_reason}
              onValueChange={(value) => setSettings({ ...settings, tip_deduction_reason: value })}
            >
              <SelectTrigger className="bg-slate-900 border-slate-700 text-slate-200">
                <SelectValue placeholder="Wähle Grund" />
              </SelectTrigger>
              <SelectContent className="bg-slate-800 border-slate-700">
                <SelectItem value="None" className="text-slate-200 focus:bg-slate-700">
                  Keine Abzüge
                </SelectItem>
                <SelectItem value="VAT" className="text-slate-200 focus:bg-slate-700">
                  Umsatzsteuer (VAT)
                </SelectItem>
                <SelectItem value="Provider Fee" className="text-slate-200 focus:bg-slate-700">
                  Provider-Gebühren
                </SelectItem>
                <SelectItem value="Tax Reserve" className="text-slate-200 focus:bg-slate-700">
                  Steuerrückstellung
                </SelectItem>
                <SelectItem value="Custom" className="text-slate-200 focus:bg-slate-700">
                  Benutzerdefiniert
                </SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* Preview Calculation */}
          {settings.tip_deduction_percent > 0 && (
            <div className="bg-slate-900/50 rounded-lg p-4 border border-slate-700">
              <h4 className="text-slate-300 font-semibold text-sm mb-3">Vorschau</h4>
              <div className="space-y-2 text-sm">
                <div className="flex justify-between text-slate-400">
                  <span>Trinkgeld Brutto:</span>
                  <span className="text-slate-200">€10,00</span>
                </div>
                <div className="flex justify-between text-slate-400">
                  <span>Abzug ({settings.tip_deduction_percent}%):</span>
                  <span className="text-red-400">-€{(10 * settings.tip_deduction_percent / 100).toFixed(2)}</span>
                </div>
                <div className="flex justify-between text-slate-200 font-semibold border-t border-slate-700 pt-2">
                  <span>Trinkgeld Netto:</span>
                  <span className="text-emerald-400">€{(10 * (1 - settings.tip_deduction_percent / 100)).toFixed(2)}</span>
                </div>
              </div>
            </div>
          )}

          {/* Save Button */}
          <Button
            onClick={saveSettings}
            disabled={saving}
            className="w-full bg-blue-600 hover:bg-blue-700"
          >
            <Save className="w-4 h-4 mr-2" />
            {saving ? 'Speichern...' : 'Einstellungen speichern'}
          </Button>
        </CardContent>
      </Card>
    </div>
  );
};

export default DeliverySettings;
